import React, { useLayoutEffect, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Card from '../../Card/Card.js';
import CardHeader from '../../Card/CardHeader.js';
import apiService from '../../../services/api.service';
import { useHistory } from 'react-router';
import CardBody from '../../Card/CardBody';
import { useState } from 'react';
import helpService from '../../../services/help.service';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { ThemeProvider } from '@material-ui/styles';
import { lightTheme } from '../../../themes/lightTheme';
import ContentFont from '../../Typography/ContentFont';
import ChartLogo from '../../parts/ChartLogo';
import LoadingOverlay from '../../parts/LoadingOverlay.js';
import ChartNoData from '../../parts/ChartNoData.js';
import { Link } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  chart: {
    height: '500px',
    marginTop: '20px',
    marginBottom: '0px',
  },
}));

function CustomsAndDutyPerSupplierChart(props) {
  const classes = useStyles();
  const [chartDataInput, setChartDataInput] = useState([]);
  const { FilterComponentData, baseQueryString } = props;
  const [scaleSet, setScaleSet] = useState(1);
  const [dataLength, setDataLength] = useState(0);
  const labelLimit = 5;
  const scaleWidth = 20;
  const [showNoData, setShowNoData] = useState(false);
  const [IsLoading, setIsLoading] = useState();

  const history = useHistory();

  function getDetailsPageUrl(freeTextSearch) {
    if (freeTextSearch) {
      return '/commercialInvoiceValuePerSupplier?s=' + freeTextSearch + '&' + baseQueryString;
    } else {
      return '/commercialInvoiceValuePerSupplier?' + baseQueryString;
    }
  }

  useEffect(() => {
    let isCancelled = false;
    if (FilterComponentData.accountId && FilterComponentData.start && FilterComponentData.end) {
      setIsLoading(true);
      apiService
        .getCustomsAndDutyPerSupplier(
          FilterComponentData.accountId,
          FilterComponentData.dateBasedOn,
          FilterComponentData.start,
          FilterComponentData.end,
          FilterComponentData.suppliers
        )
        .then((result) => {
          if (!isCancelled) {
            if (result.data.customsAndDutyPerSupplierReportItems != '' && result.data.customsAndDutyPerSupplierReportItems.length > 0) {
              setDataLength(result.data.customsAndDutyPerSupplierReportItems.length);
              if (result.data.customsAndDutyPerSupplierReportItems.length > scaleWidth) {
                setScaleSet((scaleWidth / result.data.customsAndDutyPerSupplierReportItems.length).toFixed(1));
              } else {
                setScaleSet(1);
              }
              setShowNoData(false);
            } else {
              setDataLength(0);
              setScaleSet(1);
              setShowNoData(true);
            }

            setChartDataInput(result.data.customsAndDutyPerSupplierReportItems);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) history.push('/login');
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [FilterComponentData, history]);

  function hasChartData() {
    if (chartDataInput && chartDataInput.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  useLayoutEffect(() => {
    var chart;

    if (hasChartData()) {
      var colorList = [];
      for (var i = 0; i < lightTheme.chart.colors.length; i++) {
        colorList.push(am4core.color(lightTheme.chart.colors[i]));
      }

      chart = am4core.create('CustomsAndDutyPerSupplierChart', am4charts.XYChart);
      chart.logo.disabled = true;
      chart.colors.list = colorList;

      // Add data
      chart.data = prepareStuff(chartDataInput);

      // Create axes
      let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'supplier';
      categoryAxis.title.text = '';
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.renderer.labels.template.location = dataLength < labelLimit ? 0 : 1;
      categoryAxis.renderer.labels.template.url = getDetailsPageUrl('{supplierFullName}');
      categoryAxis.title.numberFormatter.numberFormat = '$#,###.##';

      if (dataLength > labelLimit) {
        categoryAxis.renderer.labels.template.horizontalCenter = 'right';
        categoryAxis.renderer.labels.template.verticalCenter = 'middle';
        categoryAxis.fontSize = 11;
      } else categoryAxis.fontSize = 14;

      categoryAxis.renderer.labels.template.rotation = dataLength < labelLimit ? 0 : 315;
      categoryAxis.renderer.minHeight = 110;
      let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.renderer.minWidth = 90;
      valueAxis.min = 0;
      valueAxis.title.text = '';

      // Create series
      function createSeries(field, name, stacked) {
        let series = chart.series.push(new am4charts.ColumnSeries());
        series.sequencedInterpolation = true;
        series.dataFields.valueY = field;
        series.dataFields.categoryX = 'supplier';
        series.dataFields.categoryX2 = 'supplierFullName';
        series.name = name;
        series.tooltipText = '{categoryX2} - {name}: [bold]{valueY}[/]';
        series.stacked = stacked;
        series.columns.template.strokeWidth = 0;
        series.columns.template.column.fillOpacity = 0.8;
        series.fontSize = 10;
        series.columns.template.url = getDetailsPageUrl('{supplierFullName}');
      }

      createSeries('customs', 'customs', true);
      createSeries('duty', 'duty', true);

      chart.scrollbarX = new am4core.Scrollbar();
      chart.scrollbarX.visible = scaleSet < 1;
      chart.scrollbarX.end = scaleSet;

      chart.cursor = new am4charts.XYCursor();
    }
    return () => {
      if (chart) chart.dispose();
    };
  }, [chartDataInput, showNoData]);

  const prepareStuff = (data) => {
    var tempArray1 = [];
    for (var i = 0; i < data.length; i++) {
      if (data[i].supplier) {
        tempArray1.push({
          supplier: helpService.truncateString(data[i].supplier, 15),
          supplierFullName: data[i].supplier.trim(),
          customs: data[i].customs,
          duty: data[i].duty,
        });
      }
    }

    return tempArray1;
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Card chart>
        <LoadingOverlay IsLoading={IsLoading} />
        <CardHeader className={'dashboard-chart-area'}>
          <ContentFont>{showNoData ? <ChartNoData /> : <div id="CustomsAndDutyPerSupplierChart" className={classes.chart}></div>}</ContentFont>
        </CardHeader>
        <CardBody className={'dashboard-chart-title-area'}>
          <Link to={getDetailsPageUrl()}>
            <h4>Customs and Duty Value Per Supplier</h4>
          </Link>
          <ChartLogo />
        </CardBody>
      </Card>
    </ThemeProvider>
  );
}

export default CustomsAndDutyPerSupplierChart;
